/**
 * Bundles Storefront (`/bundles`)
 *
 * Public, dedicated page for browsing + buying add-on bundles. Bundles also
 * appear inside the add-ons storefront, but this page gives them their own
 * destination + nav entry. The bundle card UI is shared via <BundleCard>.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Gift } from 'lucide-react';

import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import CoinBalanceBadge from '../../components/coins/CoinBalanceBadge';
import BundleCard from '../../components/bundles/BundleCard';
import SignInPrompt from '../../components/common/SignInPrompt';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../hooks/useAuth';
import { invalidateEntitlements } from '../../hooks/useEntitlements';
import { useToast } from '../../components/common/ToastProvider';
import { ApiError } from '../../utils/apiClient';
import { getCoinRate, DEFAULT_COIN_RATE } from '../../services/settingsService';
import { fetchMyAddonDiscount } from '../../services/addonService';
import { fetchBundles, purchaseBundle, type AddonBundle } from '../../services/bundleService';

export default function BundlesStorefrontPage() {
  const { t: tb, i18n } = useTranslation('bundles');
  // Reuse the shared non-refundable confirmation copy from the add-ons namespace.
  const { t } = useTranslation('addons');
  const isRTL = i18n.language === 'ar';
  const { user } = useAuth();
  const { showToast } = useToast();

  const [bundles, setBundles] = useState<AddonBundle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [coinRate, setCoinRate] = useState<number>(DEFAULT_COIN_RATE);
  // Buyer's plan add-on discount (%); 0 when signed out or with no active plan.
  const [addonDiscount, setAddonDiscount] = useState<number>(0);
  // Non-refundable purchase confirmation (gates the paid action).
  const [confirmAction, setConfirmAction] = useState<null | { run: () => Promise<void> }>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const runConfirm = useCallback(async () => {
    if (!confirmAction) return;
    setConfirmLoading(true);
    try {
      await confirmAction.run();
    } finally {
      setConfirmLoading(false);
      setConfirmAction(null);
    }
  }, [confirmAction]);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [bundleList, rate, discount] = await Promise.all([
        fetchBundles(),
        getCoinRate(),
        user ? fetchMyAddonDiscount() : Promise.resolve(0),
      ]);
      setBundles(bundleList);
      setCoinRate(rate);
      setAddonDiscount(discount);
    } catch (err) {
      console.error('Failed to load bundles:', err);
      setError(tb('loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [tb, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBuyBundle = async (bundle: AddonBundle, method: 'SAR' | 'COINS') => {
    try {
      setPendingSlug(bundle.slug);
      setError(null);
      // Both SAR and COINS paths return an already-ACTIVE bundle purchase.
      await purchaseBundle(bundle.slug, { pricingMethod: method });
      // A bundle can include a gated add-on («مؤشرات السوق»): drop the cached access answer.
      invalidateEntitlements();
      showToast(method === 'COINS' ? tb('payWithCoinsSuccess') : tb('purchaseSuccess'), 'success');
    } catch (err) {
      console.error('Failed to buy bundle:', err);
      const code =
        err instanceof ApiError &&
        err.details &&
        typeof err.details === 'object' &&
        'code' in err.details
          ? (err.details as { code?: string }).code
          : undefined;
      if (
        method === 'COINS' &&
        ((err instanceof ApiError && err.status === 409) || code === 'INSUFFICIENT_COINS')
      ) {
        showToast(tb('insufficientCoins'), 'error');
      } else {
        showToast(tb('actionError'), 'error');
      }
    } finally {
      setPendingSlug(null);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-3 flex items-center justify-center gap-2">
            <Gift className="w-8 h-8 text-[#004D40]" aria-hidden="true" />
            {tb('pageTitle')}
          </h1>
          <p className="text-gray-500 max-w-2xl mx-auto">{tb('pageSubtitle')}</p>
          {user && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
              <span>{tb('yourBalance')}</span>
              <CoinBalanceBadge />
            </div>
          )}
          {addonDiscount > 0 && (
            <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 text-sm font-medium text-[#003930] dark:text-[#B7DDD7]">
              <Gift className="w-4 h-4" aria-hidden="true" />
              {tb('planDiscountNote', { percent: addonDiscount })}
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        )}

        {!user && <SignInPrompt message={tb('signInToBuy')} className="mb-8" />}

        {isLoading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#004D40]" />
          </div>
        ) : bundles.length === 0 ? (
          <div className="py-20 text-center text-gray-500">{tb('empty')}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bundles.map((bundle) => (
              <BundleCard
                key={bundle.id}
                bundle={bundle}
                coinRate={coinRate}
                addonDiscount={addonDiscount}
                isAuthed={!!user}
                isPending={pendingSlug === bundle.slug}
                onBuy={(method) => setConfirmAction({ run: () => handleBuyBundle(bundle, method) })}
              />
            ))}
          </div>
        )}
      </main>

      <Footer />

      <ConfirmDialog
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={runConfirm}
        title={t('confirm.title')}
        message={t('confirm.message')}
        confirmLabel={t('confirm.cta')}
        cancelLabel={t('cancel')}
        isLoading={confirmLoading}
      />
    </div>
  );
}
